import React, { useCallback, useContext, useMemo, useRef } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import auth, { AuthContext, type AuthContextValue } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
} from '@mui/material';

import useNavigationLinks from '~/hooks/useNavigationLinks';
import useToggleState from '~/hooks/useToggleState';

import { StickyVerifyCustomer } from '../StickyVerifyCustomer';
import TenantSelect from '../TenantSelect';

import NavDropdown from './NavDropdown';
import ProductsDropdown from './ProductsDropdown';
import TenantIcon from './TenantIcon';

export const TENANT_COLOURS: Record<App.Tenant['value'], { fg: string; bg: string }> = {
  lux: { fg: '#ffffff', bg: '#222' },
  scoopon: { fg: '#eee', bg: '#eb2f3a' },
  cudo: {
    fg: '#fff',
    bg: 'linear-gradient(90deg, rgb(0, 173, 179), rgb(20, 127, 196))',
  },
  deals: {
    fg: '#fff',
    bg: 'linear-gradient(90deg, rgb(20, 127, 211), rgb(0, 170, 245))',
  },
  treatme: {
    fg: '#fff',
    bg: 'linear-gradient(90deg, rgb(255, 111, 0), rgb(255, 65, 0))',
  },
  kogantravel: { fg: '#fff', bg: '#222' },
  lebusinesstraveller: { fg: '#fff', bg: '#009688' },
  zoomzoom: { fg: '#ddd', bg: '#222' },
  leagenthub: { fg: '#eee', bg: '#3f51b5' },
  leagenthub_cvfr: { fg: '#eee', bg: '#2c387e' },
};

interface SiteHeaderProps {
  tenant: App.Tenant;
}

function SiteHeader({ tenant }: SiteHeaderProps) {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { pathname } = useLocation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const navLinks = useNavigationLinks(user);

  const canShowNavLink = useCallback(
    (navLink: App.NavigationLink) => {
      if (navLink.tenants && !navLink.tenants.has(tenant.value)) {
        return false;
      }

      if (navLink.label === 'Business' || navLink.label === 'Invoice Tools') {
        return tenant.brand === 'lebusinesstraveller';
      }

      if (
        tenant.brand === 'lebusinesstraveller' &&
        ['Support Assistant', 'Commercial Ops', 'Société'].includes(navLink.label)
      ) {
        return false;
      }

      return true;
    },
    [tenant.brand, tenant.value],
  );

  const filteredNavLinks = useMemo(
    () =>
      navLinks
        .filter(canShowNavLink)
        .map((navLink) => ({ ...navLink, subLinks: navLink.subLinks?.filter(canShowNavLink) })),
    [canShowNavLink, navLinks],
  );

  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const { isToggled: isDrawerOpen, toggleOff: closeDrawer, toggle: toggleDrawer } = useToggleState();
  const { isToggled: isUserMenuOpen, toggleOn: openUserMenu, toggleOff: closeUserMenu } = useToggleState();

  const logUserOut = useCallback(() => {
    void auth
      .logout()
      .then(() => {
        history.push('/');
      })
      .catch((error) => {
        enqueueSnackbar(error instanceof Error ? error.message : 'Log out failed', {
          variant: 'error',
        });
      });
  }, [enqueueSnackbar, history]);

  return (
    <>
      <StickyVerifyCustomer />
      <AppBar
        position="static"
        sx={{
          background: TENANT_COLOURS[tenant.value].bg,
          color: TENANT_COLOURS[tenant.value].fg,
          zIndex: 9,
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Toolbar sx={{ px: { xs: '4px', md: 0 } }}>
              <IconButton color="inherit" edge="start" onClick={toggleDrawer} sx={{ display: { md: 'none' } }}>
                <MenuIcon />
              </IconButton>

              <Box pr={[1, 2]} sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton color="inherit" component={NavLink} to="/">
                  <TenantIcon />
                </IconButton>
                <TenantSelect size="large" />
              </Box>

              <ProductsDropdown />

              {!!filteredNavLinks.length && (
                <Box display={{ xs: 'none', md: 'block' }}>
                  {filteredNavLinks.map((navLink, navLinkIndex) => {
                    const hasSubLinks = !!navLink.subLinks?.length;

                    return (
                      <Box
                        key={`${navLink.href}-${navLinkIndex}`}
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        {!hasSubLinks && (
                          <Button color="inherit" to={navLink.href} component={NavLink} sx={{ textTransform: 'none' }}>
                            {navLink.label}
                          </Button>
                        )}

                        {hasSubLinks && (
                          <NavDropdown
                            buttonLabel={navLink.label}
                            items={[
                              { href: navLink.href, label: navLink.label },
                              ...(navLink.subLinks?.map((subLink) => ({
                                href: subLink.href,
                                label: subLink.label,
                              })) ?? []),
                            ]}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Toolbar>

            <Toolbar sx={{ px: { xs: 0 } }}>
              {!user && (
                <Button to="/login" component={NavLink} sx={{ textTransform: 'none' }}>
                  Login
                </Button>
              )}
              {!!user && (
                <>
                  <Button
                    className="T-account"
                    color="inherit"
                    ref={userMenuTriggerRef}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={openUserMenu}
                    sx={{ textTransform: 'none' }}
                  >
                    {user.givenName}
                  </Button>

                  <Menu
                    anchorEl={userMenuTriggerRef.current}
                    open={isUserMenuOpen}
                    autoFocus={false}
                    MenuListProps={{
                      subheader: <ListSubheader>{user.fullName}</ListSubheader>,
                    }}
                    onClose={closeUserMenu}
                  >
                    <MenuItem to="/profile" component={NavLink} onClick={closeUserMenu}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText>Account Settings</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={logUserOut}>
                      <ListItemIcon>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText>Log out</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Toolbar>
          </Stack>
        </Container>
      </AppBar>

      <Drawer
        open={isDrawerOpen}
        variant="temporary"
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box sx={{ width: '240px' }}>
          {!!filteredNavLinks.length && (
            <List>
              {filteredNavLinks.map((navLink, navLinkIndex) => (
                <React.Fragment key={`${navLink.href}-${navLinkIndex}`}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={pathname?.startsWith(navLink.href)}
                      to={navLink.href}
                      component={NavLink}
                      onClick={closeDrawer}
                    >
                      <ListItemText primary={navLink.label} />
                    </ListItemButton>
                  </ListItem>

                  {navLink.subLinks?.map((subLink, subLinkIndex) => (
                    <ListItem key={`${subLink.href}-${subLinkIndex}`} disablePadding>
                      <ListItemButton
                        selected={pathname?.startsWith(subLink.href)}
                        to={subLink.href}
                        component={NavLink}
                        onClick={closeDrawer}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText primary={subLink.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export { SiteHeader };
